import { TEST_IDS } from '@va/constants';
import { BackIconRounded } from '@va/icons';
import { useTranslate } from '@va/localization';
import {
  DataViewProps,
  DataViewToggle,
  NestedVisualizationOption,
  isNestedVisualizationOption,
} from '@va/types/report-block';
import { useTooltipContext } from '@va/ui/tooltips';
import { useIsolatedFocusedContainer } from '@va/util/misc';
import { useCallback, useRef, useState } from 'react';
import { Button } from '../button';
import { HorizontalSeparator } from '../separators';
import { Paragraph, fontWeights } from '../typography';
import { ViewListItem } from './ViewListItem';
import { ViewToggleItem } from './ViewToggleItem';

export const ViewOptionsDropdown = ({
  viewOptions = [],
  selectedView,
  onViewChange,
  getToggleState,
  onViewToggleChange,
}: DataViewProps) => {
  const translate = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [nestedOption, setNestedOption] = useState<NestedVisualizationOption | undefined>(undefined);

  const { context } = useTooltipContext();
  useIsolatedFocusedContainer({ containerRef, enabled: context.open });

  const closeTooltip = useCallback(() => {
    context.onOpenChange(false);
  }, [context]);

  return (
    <div
      ref={containerRef}
      data-testid={TEST_IDS.generic.visualizationOptions.dropdownContainer}
      className='bg-white p-6px rounded-15 min-w-300px'
    >
      {nestedOption && (
        <div className='flex gap-3 items-center py-4 px-3'>
          <Button
            color='tertiary'
            onClick={() => setNestedOption(undefined)}
            icon={(_, color) => <BackIconRounded color={color} />}
          />
          <Paragraph colorClassName='text-gray-devilSolid' weight={fontWeights.medium}>
            {translate(`all.dataViewOptionsSection.${nestedOption.view}.title`)}
          </Paragraph>
        </div>
      )}
      {!nestedOption && (
        <Paragraph className='py-4 px-18px' colorClassName='text-gray-devilSolid' weight={fontWeights.medium}>
          {translate('all.dataViewOptionsSection.title')}
        </Paragraph>
      )}
      <HorizontalSeparator className='-mx-1' />
      {nestedOption?.toggles?.map((toggle) => (
        <ViewToggleItem
          key={toggle.id}
          label={translate(`all.dataVisualization.toggle.${toggle.id}`)}
          checked={
            getToggleState?.(
              toggle.id as DataViewToggle,
              isNestedVisualizationOption(nestedOption) ? nestedOption.view : nestedOption,
            ) ??
            toggle.defaultValue ??
            false
          }
          onChange={(value) =>
            onViewToggleChange?.(
              toggle.id as DataViewToggle,
              isNestedVisualizationOption(nestedOption) ? nestedOption.view : nestedOption,
              value,
            )
          }
        />
      ))}
      <ul>
        {nestedOption?.options.map((option) => {
          return (
            <ViewListItem
              key={option}
              onClick={() => {
                onViewChange?.(option);
                closeTooltip();
              }}
              view={option}
              isSelected={option === selectedView}
            />
          );
        })}

        {!nestedOption &&
          viewOptions.map((option) => {
            if (isNestedVisualizationOption(option)) {
              return (
                <ViewListItem
                  key={option.view}
                  view={option.view}
                  onClick={() => {
                    setNestedOption(option);
                  }}
                  isSelected={false}
                  hasSubOptions={true}
                />
              );
            } else {
              return (
                <ViewListItem
                  key={option}
                  onClick={() => {
                    onViewChange?.(option);
                    closeTooltip();
                  }}
                  view={option}
                  isSelected={option === selectedView}
                />
              );
            }
          })}
      </ul>
      <Button
        data-testid={TEST_IDS.generic.buttons.close}
        onClick={closeTooltip}
        className='w-full'
        color='quinary'
        text={translate('button.close')}
      />
    </div>
  );
};
